import { useState, type FormEvent } from "react";
import type { Page } from "../types";

export function ContactForm({ onNavigate }: { onNavigate: (p: Page) => void }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [website, setWebsite] = useState(""); // honeypot
    const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setStatus("sending");
        setErrorMsg("");

        try {
            const res = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, email, message, website }),
            });

            const data = await res.json();

            if (!res.ok) {
                setStatus("error");
                setErrorMsg(data.error || "Une erreur est survenue.");
                return;
            }

            setStatus("success");
        } catch {
            setStatus("error");
            setErrorMsg("Impossible de contacter le serveur. Réessayez plus tard.");
        }
    };

    if (status === "success") {
        return (
            <section className="pt-[140px] pb-20 px-6 min-h-screen flex items-center justify-center">
                <div className="max-w-lg mx-auto text-center">
                    <div className="text-6xl mb-6">✉️</div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">Message envoyé&nbsp;!</h2>
                    <p className="text-slate-500 mb-8">
                        Merci pour votre message. Nous vous répondrons dans les meilleurs délais.
                    </p>
                    <button
                        onClick={() => onNavigate("home")}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-full transition-colors cursor-pointer border-none"
                    >
                        Retour à l'accueil
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section className="pt-[140px] pb-20 px-6">
            <div className="max-w-2xl mx-auto">
                <button
                    onClick={() => onNavigate("home")}
                    className="inline-flex items-center gap-2 text-slate-500 hover:text-orange-500 mb-8 transition-colors cursor-pointer bg-transparent border-none"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Retour
                </button>

                <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2">
                    Contactez-<span className="text-orange-500">nous</span>
                </h2>
                <p className="text-slate-500 mb-10">
                    Décrivez votre projet en quelques mots. Nous vous répondrons rapidement.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot — hidden from users */}
                    <div className="absolute -left-[9999px]" aria-hidden="true">
                        <label htmlFor="website">Website</label>
                        <input
                            id="website"
                            type="text"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            tabIndex={-1}
                            autoComplete="off"
                        />
                    </div>

                    {/* Name */}
                    <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
                            Votre nom *
                        </label>
                        <input
                            id="name"
                            type="text"
                            required
                            minLength={2}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-slate-700"
                            placeholder="Jean Dupont"
                        />
                    </div>

                    {/* Email */}
                    <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
                            Votre email *
                        </label>
                        <input
                            id="email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-slate-700"
                            placeholder="jean@exemple.fr"
                        />
                    </div>

                    {/* Message */}
                    <div>
                        <label htmlFor="message" className="block text-sm font-semibold text-slate-900 mb-2">
                            Votre message
                        </label>
                        <textarea
                            id="message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            maxLength={5000}
                            rows={5}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all resize-y text-slate-700"
                            placeholder="Parlez-nous de votre activité, de vos besoins..."
                        />
                    </div>

                    {/* Error */}
                    {status === "error" && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                            {errorMsg}
                        </div>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={status === "sending"}
                        className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white font-semibold px-8 py-3 rounded-full transition-colors text-lg shadow-[0_4px_14px_rgba(249,115,22,0.35)] cursor-pointer disabled:cursor-not-allowed border-none"
                    >
                        {status === "sending" ? "Envoi en cours…" : "Envoyer"}
                    </button>
                </form>
            </div>
        </section>
    );
}
